import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Benutzer, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, Modal, ModalFuss, Objektliste, Objektzeile,
         useBeforeUnload } from "@kundeninfo/ui";
import type { VerlassenWaechter } from "../../router.js";
import { Meldung, feldFehlerAus, formatKonfliktZeit, meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import { UnterschriftBlock } from "./Unterschrift.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich BENUTZER: Rolle und Heimatunternehmen je Person.
 *
 * Beides ist der Grund, aus dem es die Verwaltung überhaupt gibt (Betriebsbefund B2 — bis
 * dahin nur per SQL setzbar).
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Benutzerliste steht LINKS im Inhalt,
 * rechts daneben die gewählte Person. Vor Etappe 2 war der Bereich EINE Tabelle mit einem
 * Auswahlfeld und einem Speichern-Knopf je Zeile — bei zwanzig Kolleginnen zwanzig
 * gleichnamige Felder untereinander; zwischen Etappe 2 und diesem Entscheid stand die
 * Liste als zweite Ebene in der Verwaltungs-Spalte, wo sie neben dem Bereichs-Untermenü
 * redundant war.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * **Geändert wird im PANEL** (Hausregel „striktes Modalpanel", Entscheid Dirk vom
 * 06.09.2026): Das Detail rechts LIEST — Name, E-Mail, Rolle, Heimatunternehmen —, und
 * „Ändern" öffnet ein Modal mit den zwei Auswahlfeldern und der Fußleiste
 * Speichern/Abbrechen. Bis dahin standen die Felder offen im Inhalt: Man konnte sie
 * anfassen, weggehen und nie erfahren, dass etwas Ungespeichertes zurückblieb. Die
 * Fachregeln sind davon unberührt — derselbe Schreibweg, derselbe Versionsvergleich,
 * derselbe Letzter-Admin-Schutz, derselbe Verlassen-Wächter.
 *
 * **ANGELEGT wird hier nicht und GELÖSCHT auch nicht.** Benutzer entstehen bei der
 * Anmeldung über Keycloak; die Anwendung kennt keinen Schreibweg dafür (der Klient unten
 * hat genau zwei Methoden). Deshalb hat die Liste — anders als beim Unternehmen — auch
 * keinen Anlege-Knopf: Das ist die Fachlage, keine Lücke dieser Aufgabe.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste wie Formular
 * stehen im Inhalt; der Spaltenblock `vws__` gehört allein dem Bereichs-Untermenü.
 */

/** Nur die zwei Wege dieses Bereichs — so lässt er sich im Test mit zwei vi.fn zeichnen,
 *  ohne den ganzen ApiClient nachzubauen (Muster: BereichArtikelClient). */
// seit dem Layout-Editor auch die Unterschrift
export type BenutzerClient = Pick<ApiClient,
  "listUsers" | "setUserRole" | "setUserSignature" | "deleteUserSignature" |
  "userSignatureUrl">;

type BenutzerEntwurf = { role: Benutzer["role"]; homeCompanyId: string | null };

const ROLLEN: Benutzer["role"][] = ["EDITOR", "LEAD", "ADMIN"];

const ROLLEN_LABEL: Record<Benutzer["role"], string> = {
  EDITOR: "Bearbeiter (EDITOR)",
  LEAD: "Leitung (LEAD)",
  ADMIN: "Administrator (ADMIN)",
};

/**
 * Ersatztext für den Fachfehler, der ohne Feldangabe ankommen könnte.
 *
 * Im Normalfall zeigt die Maske die Meldung des SERVERS — sie ist genauer (sie nennt den
 * nächsten Schritt), und zwei Fassungen desselben Satzes liefen über die Zeit auseinander.
 * Dieser Text greift nur, wenn die 422 kein Feld mitbringt: Dann stünde sonst gar nichts da.
 */
const LETZTER_ADMIN_ERSATZ =
  "Der letzte Administrator kann nicht herabgestuft werden.";

function alsBenutzer(wert: unknown): Benutzer | null {
  if (typeof wert !== "object" || wert === null) return null;
  const b = wert as Partial<Benutzer>;
  if (typeof b.id !== "string" || typeof b.version !== "number") return null;
  return wert as Benutzer;
}

/**
 * Die Eigenschaften des Bereichs — dieselbe Gestalt wie beim Unternehmen nebenan und wie
 * bei den Bereichen der Etappe 1: `aktiv` als Riegel gegen mitlaufende Abfragen an fremden
 * Adressen, die Kennung aus der ADRESSE, ein Rückruf für den Adresswechsel. Navigiert wird
 * nicht hier — die Adresse kennt App.tsx.
 */
export type VerwaltungBenutzerProps = {
  aktiv: boolean;
  client: BenutzerClient;
  /** Die sichtbaren Konzernunternehmen — Auswahlliste für das Heimatunternehmen. */
  companies: Unternehmen[];
  /** Der geöffnete Benutzer aus der Adresse (`/verwaltung/benutzer/:id`). */
  benutzerId: string | null;
  onOeffnen: (benutzerId: string | null) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten). Weglassbar —
   *  dann fragt niemand, was für einen Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export function useVerwaltungBenutzer(
  p: VerwaltungBenutzerProps,
): VerwaltungBereichTeile {
  const { aktiv, benutzerId } = p;

  const [zeilen, setZeilen] = useState<Benutzer[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  /** Die noch nicht gespeicherten Änderungen je Person. Fehlt ein Eintrag, lebt sie aus
   *  dem geladenen Stand — nach dem Speichern wird der Entwurf deshalb GELÖSCHT. */
  const [entwuerfe, setEntwuerfe] = useState<Record<string, BenutzerEntwurf>>({});
  const [rollenFehler, setRollenFehler] = useState<Record<string, string>>({});
  const [meldungen, setMeldungen] = useState<Record<string, string>>({});
  const [konflikte, setKonflikte] =
    useState<Record<string, { von: string; wann: string; fremd: Benutzer | null }>>({});
  const [speichert, setSpeichert] = useState<string | null>(null);
  /**
   * WESSEN Änderungspanel offen steht — `null` heisst „keines" (Hausregel „striktes
   * Modalpanel", 06.09.2026: Bearbeiten läuft im Modal, nicht im Inhalt).
   *
   * Eine KENNUNG und kein blosses Ja/Nein, aus demselben Grund wie überall in dieser
   * Datei: Das Panel gehört der Person, über der es geöffnet wurde. Es ist bewusst KEIN
   * Adressbestandteil — die Adresse benennt das Objekt, nicht den Zustand der Oberfläche.
   */
  const [aendern, setAendern] = useState<string | null>(null);

  const listUsers = p.client.listUsers;
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listUsers()
      .then((antwort) => {
        if (!abgebrochen) {
          setZeilen(antwort.items);
          // Ein geglücktes Nachladen HEILT den alten Fehler (Fixrunde 1) — siehe die
          // ausführliche Begründung in unternehmen.tsx nebenan: Der Hook lebt über der
          // ganzen Anwendung, sein Zustand überlebt jeden Bereichswechsel.
          setLadeFehler(null);
        }
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Benutzer konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listUsers]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion aus
   * Aufgabe 3): Entwürfe, Fehler und Konfliktkästen sind Bearbeitungszustände ohne eigene
   * Adresse und gehören zu einer Absicht, die man verlassen hat.
   */
  useEffect(() => {
    if (aktiv) return;
    setEntwuerfe({});
    setRollenFehler({});
    setMeldungen({});
    setKonflikte({});
    setAendern(null);
    // Auch der LADEFEHLER (Fixrunde 1, wie nebenan): Beim nächsten Betreten läuft die
    // Abfrage neu — bis ihre Antwort da ist, soll die Auskunft „lädt" heissen.
    setLadeFehler(null);
  }, [aktiv]);

  /*
   * Das Änderungspanel fällt mit der KENNUNG — dieselbe Bindung wie bei der
   * Stilllegen-Rückfrage nebenan (Fixrunde 1). Der Render-Guard unten versteckt es bei
   * fremder Adresse nur; ohne dieses Nullen stünde es nach Zurück/Vorwärts unaufgefordert
   * wieder da, und sein Speichern-Knopf arbeitete an einer Person, die niemand ansieht.
   */
  useEffect(() => {
    setAendern(null);
  }, [benutzerId]);

  /** Der Name eines Heimatunternehmens fürs LESENDE Detail — „keines", wo keines
   *  hinterlegt ist, und die blosse Kennung, falls das Unternehmen nicht in der Auswahl
   *  steht (etwa weil es stillgelegt wurde): Eine leere Zeile verschwiege, dass da etwas
   *  hängt. */
  function heimatName(id: string | null): string {
    if (id === null) return "keines";
    const c = p.companies.find((x) => x.id === id);
    return c === undefined ? id : `${c.shortCode} · ${c.name}`;
  }

  function entwurfVon(z: Benutzer): BenutzerEntwurf {
    return entwuerfe[z.id] ?? { role: z.role, homeCompanyId: z.homeCompanyId };
  }

  function istGeaendert(z: Benutzer): boolean {
    const e = entwurfVon(z);
    return e.role !== z.role || e.homeCompanyId !== z.homeCompanyId;
  }

  const satz = benutzerId === null
    ? null
    : (zeilen ?? []).find((z) => z.id === benutzerId) ?? null;

  /**
   * OB gerade bearbeitet wird — als Ableitung aus der ADRESSE, nicht als eigenes Ja/Nein
   * (Etappe-1-Lektion aus Aufgabe 3): Ein freistehendes Bool überlebte dort den
   * Adresswechsel und sperrte die Spaltenliste für immer.
   */
  const bearbeitung = satz !== null && istGeaendert(satz);

  /*
   * Das ECHTE Verlassen der Seite (Tab zu, neu laden, andere Anwendung) — Bestandsverhalten
   * aus dem Paket 2a, unverändert: Es fragt bei JEDEM ungespeicherten Entwurf nach, auch
   * bei einem, der zu einer gerade nicht geöffneten Person gehört. Der Browser kennt keine
   * Adresse innerhalb der Anwendung; was er verhindern soll, ist der Verlust von Eingaben
   * überhaupt.
   */
  const etwasGeaendert = (zeilen ?? []).some(istGeaendert);
  useBeforeUnload(etwasGeaendert);

  /**
   * Der Verlassen-Wächter des ROUTERS (Hausmuster, siehe KundeStammdaten): Solange die
   * geöffnete Person ungespeicherte Änderungen trägt, fragt jede Navigation nach — der
   * Rückweg im Kopf des Einspalters ebenso wie die Zurück-Taste. (Ein Klick auf eine
   * andere Person in der Liste kommt hier nicht an: Die Zeilen sind während der
   * Bearbeitung gesperrt.)
   *
   * Bestätigtes Verlassen VERWIRFT den Entwurf (Fixrunde 1) — die ausführliche Begründung
   * steht am gleichnamigen Wächter in unternehmen.tsx: Sonst sagte die Rückfrage die
   * Unwahrheit, denn die Entwürfe liegen je Kennung und stünden beim Zurückkommen wieder da.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!bearbeitung || registriereVerlassenWaechter === undefined
      || benutzerId === null) return;
    return registriereVerlassenWaechter(() => {
      const weiter = window.confirm(
        "Dieser Benutzer hat ungespeicherte Änderungen. Sie gehen verloren. "
        + "Wirklich verlassen?");
      if (weiter) vergiss(benutzerId);
      return weiter;
    });
    // `vergiss` steht bewusst nicht in der Liste — siehe dieselbe Notiz in unternehmen.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearbeitung, registriereVerlassenWaechter, benutzerId]);

  function aendere(z: Benutzer, teil: Partial<BenutzerEntwurf>): void {
    const alt = entwurfVon(z);
    setEntwuerfe((e) => ({ ...e, [z.id]: { ...alt, ...teil } }));
    setRollenFehler((f) => ({ ...f, [z.id]: "" }));
  }

  function vergiss(id: string): void {
    setEntwuerfe((e) => {
      const rest = { ...e };
      delete rest[id];
      return rest;
    });
  }

  async function speichern(z: Benutzer): Promise<void> {
    const e = entwurfVon(z);
    setRollenFehler((f) => ({ ...f, [z.id]: "" }));
    setMeldungen((m) => ({ ...m, [z.id]: "" }));
    setKonflikte((k) => { const rest = { ...k }; delete rest[z.id]; return rest; });
    setSpeichert(z.id);
    try {
      const neu = await p.client.setUserRole(z.id, z.version, e.role, e.homeCompanyId);
      // Die ANTWORT übernehmen, nicht den eigenen Entwurf: Sie trägt die neue Version,
      // und ohne sie scheiterte das nächste Speichern derselben Person am
      // Versionsvergleich (der Server antwortete mit 409, obwohl niemand dazwischenkam).
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
      vergiss(z.id);
      // Der EINE erfolgreiche Ausgang des strikten Panels. Bei 422 oder 409 bleibt es
      // stehen: Dort ist der Fehler entstanden, dort muss er zu lesen und zu beheben sein.
      setAendern(null);
    } catch (fehler) {
      // Alles Gebrauchte VOR den Zustandsänderungen herausziehen: Innerhalb der
      // Aktualisierungsfunktionen gilt die Einengung auf ApiError nicht mehr (die
      // catch-Bindung ist nicht konstant).
      const api = fehler instanceof ApiError ? fehler : null;
      const konflikt = api?.status === 409 ? api.conflict ?? null : null;
      if (api !== null && api.status === 422) {
        const { felder, unzuordenbar } = feldFehlerAus(api, ["role", "homeCompanyId"]);
        const anRolle = felder.role
          ?? (api.code === "LETZTER_ADMIN" ? LETZTER_ADMIN_ERSATZ : undefined);
        const anHeimat = felder.homeCompanyId;
        if (anRolle !== undefined) setRollenFehler((f) => ({ ...f, [z.id]: anRolle }));
        if (anHeimat !== undefined) setMeldungen((m) => ({ ...m, [z.id]: anHeimat }));
        if (unzuordenbar || (anRolle === undefined && anHeimat === undefined)) {
          const text = api.message;
          setMeldungen((m) => ({ ...m, [z.id]: text }));
        }
      } else if (konflikt !== null) {
        setKonflikte((k) => ({
          ...k,
          [z.id]: {
            von: konflikt.changedBy,
            wann: konflikt.changedAt,
            fremd: alsBenutzer(konflikt.current),
          },
        }));
      } else {
        const text = meldungAus(fehler, "Unerwarteter Fehler beim Speichern.");
        setMeldungen((m) => ({ ...m, [z.id]: text }));
      }
    } finally {
      setSpeichert(null);
    }
  }

  /** Konfliktauflösung: den fremden Stand übernehmen — die eigene Eingabe weicht ihm auf
   *  ausdrücklichen Klick, nie stillschweigend (Muster: KundeKonditionen.tsx). */
  function fremdenStandUebernehmen(id: string): void {
    const fremd = konflikte[id]?.fremd ?? null;
    if (fremd !== null) {
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === id ? fremd : x))));
      vergiss(id);
    }
    setKonflikte((k) => { const rest = { ...k }; delete rest[id]; return rest; });
  }

  // ---------------------------------------------------------------------------
  // Die Objektliste — linke Spalte des Inhalts
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Es ist noch niemand angemeldet gewesen.</p>
      )}

      <Objektliste label="Benutzer">
        {(zeilen ?? []).map((z) => (
          <Objektzeile
            key={z.id}
            name={z.name}
            /* Die E-Mail steht in Zeile 2 und nicht als `kennung`: Sie ist zu lang für die
               erste Zeile (dort stünde sie neben dem Namen und schnitte ihn ab) und sie ist
               keine kurze Nummer. */
            meta={z.email}
            {...(z.active ? {} : { marken: ["gesperrt"] })}
            gewaehlt={z.id === benutzerId}
            gesperrt={bearbeitung}
            /* Der zugängliche Name nennt Namen UND E-Mail: Zwei Kolleginnen können denselben
               Namen tragen, die Anmeldeadresse ist eindeutig. */
            ariaLabel={`Benutzer ${z.name}, ${z.email}, anzeigen`}
            onOeffnen={() => p.onOeffnen(z.id)}
          />
        ))}
      </Objektliste>
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — das Formular GENAU EINER Person
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (benutzerId === null) {
      return (
        <p className="vw__hinweis">
          Links einen Benutzer wählen — Rolle und Heimatunternehmen stehen dann hier. Das
          Heimatunternehmen bestimmt Logo und Farbakzent im Kopf.
        </p>
      );
    }
    if (satz === null) {
      /*
       * Die dreistufige Auskunft für ein Lesezeichen auf jemanden, den die Liste nicht
       * enthält (Etappe-1-Lektion): „steht nicht in der Liste" darf erst behauptet werden,
       * wenn es eine Liste GIBT.
       */
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Diesen Benutzer gibt es nicht (mehr) — bitte links einen aus der Liste wählen.
        </p>
      );
    }

    const z = satz;
    const e = entwurfVon(z);
    const geaendert = istGeaendert(z);
    const fehlerText = rollenFehler[z.id] ?? "";
    const meldung = meldungen[z.id] ?? "";
    const konflikt = konflikte[z.id];
    /* Das Panel gehört DER Person, über der es geöffnet wurde (Fixrunde-1-Muster): Ohne
       diesen Vergleich überlebte es einen Adresswechsel, den dieses Detail nicht auslöst —
       die Zurück-Taste etwa —, und sein Speichern-Knopf schriebe an Person A, während B
       darunter stünde. */
    const panelOffen = aendern === z.id;

    /** Abbrechen — mit Rückfrage, wenn ein Entwurf offen ist, und dann VERWIRFT es ihn.
     *  Anders sagte die Rückfrage die Unwahrheit: Die Entwürfe liegen je Kennung und
     *  stünden beim nächsten Öffnen wieder da (dieselbe Lehre wie beim Verlassen-Wächter
     *  oben, Fixrunde 1). */
    function abbrechen(): void {
      // Kein Ausgang, solange geschrieben wird — Escape wie Knopf (Fixrunde 1).
      if (speichert === z.id) return;
      if (!darfAbbrechen(geaendert)) return;
      vergiss(z.id);
      setKonflikte((k) => { const rest = { ...k }; delete rest[z.id]; return rest; });
      setRollenFehler((f) => ({ ...f, [z.id]: "" }));
      setMeldungen((m) => ({ ...m, [z.id]: "" }));
      setAendern(null);
    }

    return (
      /*
       * Eigene Identität je Person (Etappe-1-Lektion `key={objekt.id}`): Ohne diesen
       * Schlüssel nähme ein Adresswechsel den DOM-Zustand dieses Formulars auf die nächste
       * Person mit.
       */
      <div key={z.id} className="vw__eintrag">
        <div className="vw__eintrag-kopf">
          <span className="vw__eintrag-name">{z.name}</span>
          <span className="vw__marke">{z.active ? "aktiv" : "gesperrt"}</span>
        </div>
        <p className="vw__eintrag-zusatz">{z.email}</p>

        {/*
          * Das Detail LIEST (Hausregel „striktes Modalpanel", 06.09.2026): Rolle und
          * Heimatunternehmen stehen hier als Text, geändert wird im Panel. Bis dahin waren
          * es zwei Auswahlfelder mitten im Inhalt — man konnte sie anfassen, weggehen und
          * nie erfahren, dass etwas Ungespeichertes zurückblieb.
          *
          * Gezeigt wird der ENTWURF, wo einer liegt: Bricht jemand ab, ist er weg; steht er
          * noch (etwa nach einem abgelehnten Speichern), soll das Detail nicht behaupten,
          * es gelte schon wieder der Serverstand. Die Marke daneben sagt es ausdrücklich.
          */}
        <span className="vw__eintrag-wert">{ROLLEN_LABEL[e.role]}</span>
        <span className="vw__eintrag-zusatz">
          Heimatunternehmen: {heimatName(e.homeCompanyId)}
        </span>
        {geaendert && <span className="vw__marke">ungespeichert</span>}

        <div className="vw__eintrag-aktionen">
          <button type="button" className="vw__knopf"
            aria-label={`${z.name} ändern`}
            onClick={() => setAendern(z.id)}>
            Ändern
          </button>
          {meldung !== "" && !panelOffen && (
            <span className="vw__zeilenfehler" role="alert">{meldung}</span>
          )}
        </div>

        {/* Die UNTERSCHRIFT (Layout-Editor Etappe 1) — sofort wirksam wie die Firmenlogos;
            sie gehört nicht ins Änderungspanel, weil dort ein Abbrechen sie nicht
            zurücknähme. Die Antwort ersetzt die Zeile (Version unverändert). */}
        <UnterschriftBlock client={p.client} benutzer={z}
          onGeaendert={(neu) => setZeilen((alt) => (alt === null ? alt
            : alt.map((x) => (x.id === neu.id ? neu : x))))} />

        {/* Das Änderungspanel steht bei seinem Knopf, also im Detail — und gehört genau
            dieser Person (siehe `panelOffen`). */}
        {panelOffen && (
          <Modal titel={`${z.name} ändern`} onAbbrechen={abbrechen}
            abbrechenGesperrt={speichert === z.id}>
            {meldung !== "" && <Meldung text={meldung} />}

            <div className="vw__auswahl">
              <label className="vw__label" htmlFor={`vw-rolle-${z.id}`}>
                Rolle von {z.name}
              </label>
              <select id={`vw-rolle-${z.id}`} className="vw__select" value={e.role}
                onChange={(ev) => aendere(z, {
                  role: ev.target.value as Benutzer["role"],
                })}>
                {ROLLEN.map((r) => (
                  <option key={r} value={r}>{ROLLEN_LABEL[r]}</option>
                ))}
              </select>
              {fehlerText !== "" && (
                <span className="vw__feldfehler" role="alert">{fehlerText}</span>
              )}
            </div>

            <div className="vw__auswahl">
              <label className="vw__label" htmlFor={`vw-heimat-${z.id}`}>
                Heimatunternehmen von {z.name}
              </label>
              <select id={`vw-heimat-${z.id}`} className="vw__select"
                value={e.homeCompanyId ?? ""}
                onChange={(ev) => aendere(z, {
                  homeCompanyId: ev.target.value === "" ? null : ev.target.value,
                })}>
                <option value="">keines</option>
                {p.companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
                ))}
              </select>
            </div>

            {konflikt !== undefined && (
              <div className="vw__konflikt" role="alert">
                <p className="vw__konflikt-titel">
                  {konflikt.von} hat diesen Benutzer inzwischen geändert
                  ({formatKonfliktZeit(konflikt.wann)}). Deine Eingaben sind nicht
                  verloren — entscheide, welcher Stand gilt.
                </p>
                <button type="button" className="vw__knopf"
                  onClick={() => fremdenStandUebernehmen(z.id)}>
                  Fremden Stand übernehmen
                </button>
              </div>
            )}

            <ModalFuss speichernText="Speichern"
              speichernGesperrt={!geaendert || speichert === z.id}
              /* Nur während des laufenden Schreibens — nicht an `geaendert`: Ein Panel
                 ohne Eingabe muss gerade DANN abbrechbar sein. */
              abbrechenGesperrt={speichert === z.id}
              speichernLabel={`Änderungen an ${z.name} speichern`}
              onSpeichern={() => { void speichern(z); }}
              onAbbrechen={abbrechen} />
          </Modal>
        )}
      </div>
    );
  })();

  return { liste, detail };
}
